import type { HTMLAttributes, PropsWithChildren, ReactChild, ReactNode } from 'react';

import type { ButtonProps, SubmitButtonProps } from '@capsule/consumer-design-system';
import { ButtonWithLinkStyle, ScreenReaderOnly, Typography, XIconButton } from '@capsule/consumer-design-system';

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { AnimationProps } from 'framer-motion';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import type { CTAGroupProps } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/CTAGroup';
import { CTAGroup } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/CTAGroup';
import { medications } from 'src/app/router/paths';
import { CAPSULE_ICON_STRINGS } from 'src/consts';
import { FEATURE_LAYOUT_TITLE } from 'src/consts/testIDs';

import { INITIAL_ANIMATION_DELAY, LAST_ANIMATION_DELAY } from './constants';

export interface SimpleFeatureLayoutBaseProps {
  className?: string;
  /** A required key prop necessary for Framer Motion. */
  keyName: string;
  title: ReactChild;
  description?: ReactChild;

  /**
   * @default { isRendered: true, onClick: noop }
   */
  backButton?:
    | {
        isRendered?: true;
        onClick: () => void;
      }
    | {
        isRendered: false;
      };

  /** If not defined, no close button will render */
  closeButtonClickHandler?: () => void;
  shouldDisplayCapsuleLogo?: boolean;
  shouldCapsuleLogoLinkToHome?: boolean;

  preTitleHeader?: ReactChild;

  body: {
    content: ReactNode;
    type?: 'artwork' | 'other';
  };
}

type ConditionalCTAGroupProps<T> = T extends { cta1: Omit<ButtonProps, 'variant'> | Omit<SubmitButtonProps, 'variant'> }
  ? CTAGroupProps
  : Partial<CTAGroupProps>;

type SimpleFeatureLayoutProps = SimpleFeatureLayoutBaseProps & ConditionalCTAGroupProps<SimpleFeatureLayoutBaseProps>;

const LayoutContainer = styled.div`
  --mobileGutterSizeOnboardingLayout: ${({ theme }) => theme.space.s5};

  color: ${({ theme }) => theme.colors.capsuleBlue50};
  display: flex;
  flex-direction: column;
  height: 100%;

  ${({ theme }) => theme.mediaQueries.small} {
    gap: ${({ theme }) => theme.space.s7};
    position: relative;
    width: 100%;
    max-width: 600px;
    margin: ${({ theme }) => theme.space.s8} auto 0;
    padding-left: ${({ theme }) => theme.space.s7};
    padding-right: ${({ theme }) => theme.space.s7};
  }
`;

const ContentContainer = styled.div<{ isRenderingArtwork: boolean; hasButtonContainer?: boolean }>`
  padding-top: ${({ hasButtonContainer, theme }) => (hasButtonContainer ? 0 : theme.space.s7)};

  ${({ theme }) => theme.mediaQueries.smallDown} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${({ isRenderingArtwork, theme }) => (isRenderingArtwork ? theme.space.s5 : 0)};
    flex-grow: 1;
    overflow-y: ${({ isRenderingArtwork }) => (isRenderingArtwork ? 'hidden' : 'auto')};
    padding-left: var(--mobileGutterSizeOnboardingLayout);
    padding-right: var(--mobileGutterSizeOnboardingLayout);
    padding-bottom: ${({ theme }) => theme.space.s2};
  }

  ${({ theme }) => theme.mediaQueries.small} {
    padding: 0;
  }
`;

const ContentHeaderContainer = styled.div<{ isRenderingArtwork: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.s3};

  /* Just giving breathing space between back button and nav header */
  margin-top: ${({ theme }) => theme.space.s2};
  margin-bottom: ${({ isRenderingArtwork, theme }) => (isRenderingArtwork ? 0 : theme.space.s6)};

  ${({ theme }) => theme.mediaQueries.small} {
    margin-top: 0;
    margin-bottom: ${({ theme }) => theme.space.s7};
  }
`;

const ContentHeaderButtonGroupContainer = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

// TODO: Abstract to reusable IconButton component
const IconButton = styled.button.attrs({ type: 'button' })`
  appearance: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.capsuleBlue50};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;

  & > svg {
    width: 24px;
    height: 24px;
  }
`;

const MobileBackButton = (props: Omit<HTMLAttributes<HTMLButtonElement>, 'type'>) => (
  <IconButton
    {...props}
    css={css`
      ${({ theme }) => theme.mediaQueries.small} {
        display: none;
      }
    `}
  >
    <ScreenReaderOnly>Go Back</ScreenReaderOnly>

    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        d="M14.7071 4.29289C15.0976 3.90237 15.7308 3.90237 16.1213 4.29289C16.5118 4.68342 16.5118 5.31658 16.1213 5.70711L9.82843 12L16.1213 18.2929C16.5118 18.6834 16.5118 19.3166 16.1213 19.7071C15.7308 20.0976 15.0976 20.0976 14.7071 19.7071L7 12L14.7071 4.29289Z"
        fill="currentColor"
      />
    </svg>
  </IconButton>
);

const NonMobileBackButton = styled(ButtonWithLinkStyle)`
  & > * {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.space.s1};
  }

  & svg {
    width: 18px;
    height: 18px;
  }

  ${({ theme }) => theme.mediaQueries.smallDown} {
    display: none;
  }
`;

const StyledHeader = styled.header`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.s2};
  ${({ theme }) => theme.mediaQueries.small} {
    gap: ${({ theme }) => theme.space.s3};
  }

  & > * {
    margin: 0;
  }
`;

const StepBody = styled(Typography).attrs({ as: motion.div, variant: 'body' })<{ isRenderingArtwork: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: ${({ isRenderingArtwork }) => (isRenderingArtwork ? 'center' : 'flex-start')};
  align-items: ${({ isRenderingArtwork }) => (isRenderingArtwork ? 'center' : 'flex-start')};
  gap: ${({ theme }) => theme.space.s5};

  & form {
    /** Bottom padding is a user experience boost to allow for more scroll space on mobile. */
    padding-bottom: ${({ theme }) => theme.space.s5};
  }

  & > * {
    width: 100%;
  }

  & > svg {
    margin: 0 auto;
    width: 80%;
  }

  ${({ theme }) => theme.mediaQueries.smallDown} {
    flex-grow: 1;
    height: 0;

    & > svg {
      margin: unset;
      width: 100vw;
    }

    & > *:last-child {
      padding-bottom: ${({ theme }) => theme.space.s6};
    }
  }
`;

const buttonGroupContainerAnimationProps: AnimationProps = {
  initial: { opacity: '0%' },
  animate: { opacity: '100%' },
  transition: { duration: 0.3, ease: 'easeIn', delay: LAST_ANIMATION_DELAY },
};

const getPrimaryAnimProps = ({
  delay,
  shouldReduceMotion,
}: {
  /** In seconds */
  delay: number;
  shouldReduceMotion: boolean;
}): AnimationProps => ({
  initial: { opacity: '0%', translateY: shouldReduceMotion ? '0rem' : '0.8rem' },
  animate: { opacity: '100%', translateY: '0rem' },
  transition: { duration: 0.3, ease: 'easeIn', delay },
});

export const SimpleFeatureLayout = ({
  className,
  backButton,
  closeButtonClickHandler,
  shouldDisplayCapsuleLogo,
  shouldCapsuleLogoLinkToHome,
  preTitleHeader,
  title,
  description,
  body,
  keyName,
  cta1,
  cta2,
  error,
}: SimpleFeatureLayoutProps) => {
  const history = useHistory();
  const shouldReduceMotion = !!useReducedMotion();

  const hasBackButton = backButton?.isRendered ?? true;

  const back = () => {
    if (backButton && 'onClick' in backButton) {
      backButton.onClick();
    }

    history.goBack();
  };

  const hasButtonContainer = hasBackButton || !!closeButtonClickHandler || shouldDisplayCapsuleLogo;

  const isRenderingArtwork = body.type === 'artwork';

  return (
    <LayoutContainer className={className}>
      <ContentContainer isRenderingArtwork={isRenderingArtwork} hasButtonContainer={hasButtonContainer}>
        <ContentHeaderContainer isRenderingArtwork={isRenderingArtwork}>
          {hasButtonContainer && (
            <ContentHeaderButtonGroupContainer
              {...buttonGroupContainerAnimationProps}
              key={`simple-feature-layout-back-button-${keyName}`}
            >
              {hasBackButton && (
                <>
                  <MobileBackButton onClick={back} />

                  <NonMobileBackButton onClick={back}>
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path
                        d="M14.7071 4.29289C15.0976 3.90237 15.7308 3.90237 16.1213 4.29289C16.5118 4.68342 16.5118 5.31658 16.1213 5.70711L9.82843 12L16.1213 18.2929C16.5118 18.6834 16.5118 19.3166 16.1213 19.7071C15.7308 20.0976 15.0976 20.0976 14.7071 19.7071L7 12L14.7071 4.29289Z"
                        fill="currentColor"
                      />
                    </svg>
                    Back
                  </NonMobileBackButton>
                </>
              )}

              {shouldDisplayCapsuleLogo &&
                (shouldCapsuleLogoLinkToHome ? (
                  <Link aria-label="Go To Medications Home Page" to={medications.toPath()} data-cy="logo">
                    <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.capsule_logo} block />
                  </Link>
                ) : (
                  <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.capsule_logo} block />
                ))}

              {closeButtonClickHandler && (
                <XIconButton
                  css={css`
                    color: ${({ theme }) => theme.colors.capsuleGray50};
                    margin-left: auto;
                    padding: ${({ theme }) => theme.space.s2} 0;
                  `}
                  helperText="Return To Home"
                  onClick={closeButtonClickHandler}
                />
              )}
            </ContentHeaderButtonGroupContainer>
          )}

          <StyledHeader>
            {preTitleHeader && (
              <Typography
                as={motion.div}
                variant="overline"
                {...getPrimaryAnimProps({ shouldReduceMotion, delay: INITIAL_ANIMATION_DELAY })}
                css={css`
                  margin-top: ${({ theme }) => theme.space.s4};
                `}
              >
                {preTitleHeader}
              </Typography>
            )}
            <Typography
              as={typeof title === 'string' ? motion.h2 : motion.div}
              variant={{ base: 'heading1', small: 'displayXl' }}
              data-testid={FEATURE_LAYOUT_TITLE}
              {...getPrimaryAnimProps({ shouldReduceMotion, delay: INITIAL_ANIMATION_DELAY })}
            >
              {title}
            </Typography>

            {description && (
              <Typography
                as={motion.p}
                variant="body"
                {...getPrimaryAnimProps({ shouldReduceMotion, delay: LAST_ANIMATION_DELAY })}
              >
                {description}
              </Typography>
            )}
          </StyledHeader>
        </ContentHeaderContainer>

        <StepBody
          isRenderingArtwork={isRenderingArtwork}
          {...getPrimaryAnimProps({ shouldReduceMotion, delay: LAST_ANIMATION_DELAY })}
        >
          {body.content}
        </StepBody>
      </ContentContainer>

      {cta1 && (
        /*
          At this point, it's no longer that "Simple" of a feature layout anymore. Sorry. Conditionally
          render the CTAGroup we can have a page with no CTAs or online CTAs if we so desire.
        */
        <CTAGroup
          cta1={cta1}
          cta2={cta2}
          error={error}
          {...getPrimaryAnimProps({ shouldReduceMotion, delay: LAST_ANIMATION_DELAY })}
        />
      )}
    </LayoutContainer>
  );
};

const FixedTopNavAwareStyledDiv = styled.div<{ navHeightOverride?: string }>`
  position: fixed;
  margin: 0 auto;
  width: 100%;

  /** we want a defined height to we can have a pinned footer without using position static and suffering z-index. */
  height: calc(100% - ${({ theme, navHeightOverride }) => navHeightOverride || theme.space.loggedInHeaderNavHeight});

  ${({ theme }) => theme.mediaQueries.small} {
    height: 100%;
  }

  box-sizing: border-box;
  & * {
    box-sizing: border-box;
  }
`;

const StyledMotionDiv = styled(motion.div)<{ navHeightOverride?: string }>`
  height: 100%;
  ${({ theme }) => theme.mediaQueries.small} {
    overflow-y: auto;
    max-height: calc(
      100% - ${({ theme, navHeightOverride }) => navHeightOverride || theme.space.loggedInHeaderNavHeight}
    );
    padding-bottom: ${({ theme }) => theme.space.s4};
    height: auto;
  }
`;

export const SimpleFeatureLayoutPageContainer = ({
  children,
  key,
  navHeightOverride,
}: PropsWithChildren<{
  /** This is required for exit animtations to function properly.
   * @see https://www.framer.com/motion/animate-presence/##exit-animations
   */
  key: string;
  navHeightOverride?: string;
}>) => (
  <FixedTopNavAwareStyledDiv navHeightOverride={navHeightOverride}>
    <AnimatePresence exitBeforeEnter>
      <StyledMotionDiv
        navHeightOverride={navHeightOverride}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeIn' }}
        key={key}
      >
        {children}
      </StyledMotionDiv>
    </AnimatePresence>
  </FixedTopNavAwareStyledDiv>
);
